import React, { useRef, useEffect } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import CustomButton from 'components/CustomButton';
import StartAssessmentDialog from 'dialogs/StartAssessmentDialog';
import { SimpleTabs } from 'components/Tabs';
import { useServices, useSnackbar } from 'contexts';
import { getChallengesByTags, starredCodeChallengeById } from 'services';
import { getTwoDigitNo, Session } from 'utils';
import { ChallengeDifficulty } from './constant';
import { useNavigateWithClient } from 'hooks';
import { CustomTooltip } from 'components/InfoTooltip';
import NoAccessTooltip from 'components/NoAccessTooltip';
import { useSelectedBatchRemoved } from 'announcementStore';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#fff',
    boxShadow: "0px 0px 4px 0px #00000026",
    borderRadius: '4px',
    padding: theme.spacing(7,10),
  },
  header: {
    borderBottom: '1px solid #8692A3',
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '22px',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  content: {
    marginTop: theme.spacing(7),
  },
  cards: {
    display: 'flex', flexDirection: 'column', gap: '20px', 
    flexWrap: 'wrap', marginTop: '12px'
  },
  cardRoot: {
    border: '2px solid',
    width: '100%',
    borderColor: theme.palette.neutral['clr-200'],
    overflow: 'hidden',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(7, 6),
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary['clr-100'],
    }
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  cardImgContainer: {
    width: '56px', height: '56px',
    borderRadius: '8px',
    border: '1px solid #CCD4DE',
    overflow: 'hidden',
  },
  skeletonCardRoot: {
    width: '100%',
    border: '2px solid',
    borderColor: theme.palette.neutral['clr-200'],
    overflow: 'hidden',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(7, 6),
  },
  skeleton: {
    borderRadius: '4px',
  },
}));

const Card = ({
  _id, attempts=[], title='', starredBy=[], entryPoint=null, feedbackAttemptId=null
}) => {
  const classes = useStyles();
  const service = useServices();
  const navigateWithClient = useNavigateWithClient();
  const removed = useSelectedBatchRemoved();

  const [starred, setStarred] = React.useState(false);
  const [startInterview , setStartInterview] = React.useState(false);

  const handleStarred = async() => {
    try {
      await service.callService(starredCodeChallengeById, _id);
      setStarred((prev) => !prev);
    } catch (err) {
      console.error(err.message);
    }
  };

  React.useEffect(()=>{
    setStarred(starredBy?.includes(Session.user._id.toString()));
  }, [starredBy, _id]);
  
  return (
    <Box className={classes.cardRoot}>
      <Box className={classes.cardContent}>
        <Typography variant='h4-medium'>
          {title}
        </Typography>
        <Box display='flex' gap={2} mt={1} alignItems='center'>
          <Typography variant='body01-bold' color='neutral.clr-600'>
            Attempts: {attempts?.length}
          </Typography>
          {/* <IconButton
              onClick={handleStarred}
              color={starred ? "warning" : "default"}
              style={{ padding: 0 }}
            >
            {starred ? <Star /> : <StarBorder />}
          </IconButton> */}
        </Box>
      </Box>
      {
        !attempts?.length ?
          <NoAccessTooltip show={removed}>
            <CustomButton onClick={() => setStartInterview(true)} variant='contained' color='primary' disabled={removed}>
              Attempt now
            </CustomButton>
          </NoAccessTooltip>
          :
          <Box style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <NoAccessTooltip show={removed}>
              <CustomButton onClick={() => setStartInterview(true)} variant='outlined' color='primary' disabled={removed}>
                Reattempt
              </CustomButton>
            </NoAccessTooltip>
            {
              feedbackAttemptId && 
                <Typography
                  onClick={() => navigateWithClient(`/performance/4c/${feedbackAttemptId}`)}
                  variant='body01-link'
                  style={{color: '#1961BF'}}
                >
                  View feedback
                </Typography>
            }
          </Box>
      }
        <StartAssessmentDialog
            onClose={() => setStartInterview(false)}
            open={startInterview}
            template={{ _id : _id , name : title}}
            codingInterview={true}
            stateParams={entryPoint}
          />
    </Box>
  );
};

const CardSkeleton = () => {
  const classes = useStyles();
  
  return (
    <Box className={classes.skeletonCardRoot}>
      <Box className={classes.cardContent}>
        <Skeleton variant="text" width={200} height={32} className={classes.skeleton} />
        <Box display='flex' gap={2} mt={1} alignItems='center'>
          <Skeleton variant="text" width={100} height={24} className={classes.skeleton} />
        </Box>
      </Box>
      <Skeleton variant="rectangular" width={120} height={40} className={classes.skeleton} />
    </Box>
  );
};

const SectionSkeleton = () => {
  const classes = useStyles();
  
  return (
    <Box mt={4}>
      <Skeleton variant="text" width={150} height={28} className={classes.skeleton} />
      <Box className={classes.cards}>
        {[...Array(3)].map((_, index) => (
          <CardSkeleton key={`card-skeleton-${index}`} />
        ))}
      </Box>
    </Box>
  );
};

const Challenges = ({
  company=null, subTopic=null, handleBack=()=>{}, initialFilters=null, entryPoint=null
}) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = React.useState(0);
  const service = useServices();
  const snackbar = useSnackbar();

  const [loading, setLoading] = React.useState(false);
  const [challenges, setChallenges] = React.useState([]);
  const [completionRate, setCompletionRate] = React.useState(0);
  const [isManualTabChange, setIsManualTabChange] = React.useState(false);
  
  const sectionRefs = useRef({});
  const observerRef = useRef(null);


  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const filters = {
          ...initialFilters,
          tags: [],
          includeCompanyTags: company?._id ? true : false
        };
        if(company?._id) filters.tags.push(company?._id);
        if(subTopic?._id) filters.tags.push(subTopic?._id);

        const result = await service.callService(
          getChallengesByTags, filters
        );

        let totalChallenges = 0;
        let totalCompleted = 0;

        const filteredChallenge = ["very_easy", "easy", "medium", "hard"]
          .map((key, index) => {
            const question = result?.[key] || [];
            if (question.length === 0) return null;
            
            const uniqueChallengeIds = new Set();
            totalChallenges+=question.length

            question.forEach(({ attempts }, i) => {

              if(attempts.length) totalCompleted+=1;
              attempts?.forEach(({ _id, codeChallenges, feedback }) => {
                codeChallenges.forEach(challengeId => uniqueChallengeIds.add(challengeId));
                if(feedback){
                  question[i].feedbackAttemptId = _id;
                }
              });
            });

            const totalAttempted = uniqueChallengeIds.size;
            
            return { label: ChallengeDifficulty[key], question, totalAttempted };
          })
          .filter(Boolean);

        setChallenges(filteredChallenge);
        setCompletionRate((totalCompleted/totalChallenges)*100);
        
      } catch (error) {
        console.log(error);
        snackbar.error("Uh Oh! Unable to get the topics details.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // This effect handles scrolling to the section when tab is clicked
  useEffect(() => {
    if (challenges.length > 0 && sectionRefs.current[activeTab] && isManualTabChange) {
      sectionRefs.current[activeTab].scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      
      const timer = setTimeout(() => {
        setIsManualTabChange(false);
      }, 1200); 
      
      return () => clearTimeout(timer);
    }
  }, [activeTab, challenges.length, isManualTabChange]);

  useEffect(() => {
    if (!loading && challenges.length > 0) {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (isManualTabChange) return;

          let maxVisibility = 0;
          let mostVisibleSection = null;
          
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const visibleHeight = Math.min(
                entry.boundingClientRect.bottom, 
                window.innerHeight
              ) - Math.max(
                entry.boundingClientRect.top, 
                0
              );
              
              const visibilityRatio = visibleHeight / entry.boundingClientRect.height;
              
              if (visibilityRatio > maxVisibility) {
                maxVisibility = visibilityRatio;
                mostVisibleSection = entry.target;
              }
            }
          });

          if (mostVisibleSection && maxVisibility > 0.5) {
            const visibleSectionIndex = parseInt(mostVisibleSection.dataset.index, 10);
            if (!isNaN(visibleSectionIndex) && visibleSectionIndex !== activeTab) {
              setActiveTab(visibleSectionIndex);
            }
          }
        },
        { 
          threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9], 
          rootMargin: '0px'
        }
      );
      
      Object.keys(sectionRefs.current).forEach(index => {
        if (sectionRefs.current[index]) {
          sectionRefs.current[index].dataset.index = index;
          observerRef.current.observe(sectionRefs.current[index]);
        }
      });
    }
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, challenges, activeTab, isManualTabChange]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setIsManualTabChange(true);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.title}>
          <ArrowBackIcon style={{ cursor: 'pointer', color: '#0062E1' }} onClick={handleBack}/>
          <Typography variant='h5-medium'>
            {subTopic?.name?.charAt(0).toUpperCase() + subTopic?.name?.slice(1)}
          </Typography>
        </Box>
        <CustomTooltip
          component='It represents your progress for this topic. It is based on your attempted questions'
        >
          <Typography
            variant='h6-medium'
            color='primary.clr-300'
            style={{ borderBottom: '2px dotted #135EBF'}}
          >
            Completed: {completionRate.toFixed(0)}%
          </Typography>
        </CustomTooltip>
      </Box>

      {loading ? (
        <Box style={{display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '16px'}}>
          <Skeleton variant="text" width={120} height={32} className={classes.skeleton} />
          <Skeleton variant="rectangular" width="100%" height={48} className={classes.skeleton} />
        </Box>
      ) : challenges.length > 1 ? (
        <Box style={{display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '16px'}}>
          <Typography variant='h4-semiBold' color='primary.clr-400'>Challenges</Typography>
          <SimpleTabs
            options={challenges.map(({label}, i)=>({id: i, label}))}
            value={activeTab}
            onChange={handleTabChange}
          />
        </Box>
      ) : null}

      <Box className={classes.content}>
        {loading ? (
          [...Array(2)].map((_, index) => (
            <SectionSkeleton key={`section-skeleton-${index}`} />
          ))
        ) : (
          challenges.map(({label, totalAttempted, question}, i) => {
            return(
              <Box 
                mt={4} 
                key={i}
                ref={el => sectionRefs.current[i] = el}
                data-index={i}
              >
                <Typography variant='h5-medium' color='primary.clr-400'>
                  {label}: {getTwoDigitNo(totalAttempted)}/{getTwoDigitNo(question?.length)}
                </Typography>
                <Box className={classes.cards}>
                  {
                    question.map((data, j) => 
                        <Card
                          key={j}
                          {...data}
                          attempts={data.attempts}
                          entryPoint={entryPoint}
                        /> 
                      )
                  }
                </Box>
              </Box>
            )
          })
        )}
      </Box>
    </Box>
  )
}

export default Challenges;